import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Alert, Button, Card, Field, Select, Spinner, Textarea } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { ATTACHMENT_ACCEPT, uploadAttachment } from '../../lib/attachments';
import { errorMessage } from '../../lib/errors';
import { type Priority, PRIORITIES, priorityName, useRequestForm } from './requests-data';

/** Formulario de solicitud de proyecto (guía 7.2): lo pide cualquiera; lo aprueban su jefe, IT y el Gerente de IT. */
export function NewRequestPage() {
  const form = useRequestForm();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [benefit, setBenefit] = useState('');
  const [area, setArea] = useState('');
  const [urgency, setUrgency] = useState<Priority>('media');
  const [idealDate, setIdealDate] = useState('');
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!form.data) return form.error ? <Alert>{errorMessage(form.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  const data = form.data;
  const orgId = organizationId ?? data.defaultOrganizationId;
  const orgName = data.organizations.find((o) => o.id === orgId)?.name;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await api<{ id: string; number: number }>('/api/proyectos/solicitudes', {
        json: {
          title: title.trim(),
          description: description.trim(),
          expectedBenefit: benefit.trim(),
          affectedArea: area.trim() || null,
          perceivedUrgency: urgency,
          idealDate: idealDate || null,
          ...(data.isVp ? { organizationId: orgId } : {}),
        },
      });
      // Los adjuntos se suben una vez creada la solicitud (son de ella).
      const failed: string[] = [];
      for (const file of files) await uploadAttachment('project_request', created.id, file).catch(() => failed.push(file.name));
      await qc.invalidateQueries({ queryKey: ['solicitudes'] });
      void navigate(`/proyectos/solicitudes/${created.number}`, { state: { created: true, failedFiles: failed } });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <Link to="/proyectos/solicitudes" className="text-sm text-marca hover:underline">
          ← {t('sp.titulo')}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-tinta">{t('sp.nueva')}</h1>
        <p className="text-sm text-slate-600">{t('sp.nueva_ayuda')}</p>
      </div>
      <Alert tone="info">{data.manager ? t('sp.aprobara', { jefe: data.manager }) : t('sp.sin_jefe')}</Alert>
      <Card>
        <form onSubmit={submit} className="space-y-4">
          {error && <Alert>{error}</Alert>}
          <Field id="sp-titulo" label={t('sp.campo_titulo')} required minLength={3} maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
          <Textarea
            id="sp-problema"
            label={t('sp.campo_problema')}
            required
            rows={5}
            maxLength={10_000}
            placeholder={t('sp.campo_problema_ayuda')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Textarea
            id="sp-beneficio"
            label={t('sp.campo_beneficio')}
            required
            rows={3}
            maxLength={5_000}
            placeholder={t('sp.campo_beneficio_ayuda')}
            value={benefit}
            onChange={(e) => setBenefit(e.target.value)}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="sp-area" label={t('sp.campo_area')} maxLength={200} value={area} onChange={(e) => setArea(e.target.value)} />
            <Field id="sp-fecha" type="date" label={t('sp.campo_fecha')} value={idealDate} onChange={(e) => setIdealDate(e.target.value)} />
          </div>
          {data.isVp ? (
            <Select id="sp-linea" label={t('sp.campo_linea')} value={orgId} onChange={(e) => setOrganizationId(e.target.value)}>
              {data.organizations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.groupName} › {o.name}
                </option>
              ))}
            </Select>
          ) : (
            <p className="text-sm text-slate-600">{t('sp.linea_propia', { linea: orgName ?? '—' })}</p>
          )}
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-slate-700">{t('sp.campo_urgencia')}</legend>
            <div className="flex flex-wrap gap-2">
              {PRIORITIES.map((p) => (
                <label key={p} className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${urgency === p ? 'border-marca bg-marca/5' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <input type="radio" name="sp-urgencia" value={p} checked={urgency === p} onChange={() => setUrgency(p)} className="accent-marca" />
                  {priorityName(p)}
                </label>
              ))}
            </div>
            <p className="text-xs text-slate-500">{t('sp.campo_urgencia_ayuda')}</p>
          </fieldset>
          <div className="space-y-1">
            <label htmlFor="sp-adjuntos" className="block text-sm font-medium text-slate-700">
              {t('sp.campo_adjuntos')}
            </label>
            <input id="sp-adjuntos" type="file" multiple accept={ATTACHMENT_ACCEPT} onChange={(e) => setFiles([...(e.target.files ?? [])])} className="text-sm" />
            {files.length > 0 && <p className="text-xs text-slate-500">{t('sp.adjuntos_elegidos', { n: files.length })}</p>}
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || title.trim().length < 3 || !description.trim() || !benefit.trim()}>
              {busy ? t('sp.enviando') : t('sp.enviar')}
            </Button>
            <Link to="/proyectos/solicitudes" className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              {t('comun.cancelar')}
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}
